<script setup lang="ts">
/**
 * Teams — groups of people kept in DOIT AI (ADR 0063).
 *
 * Entra groups are Group IT's to create, and the teams people actually work
 * in (Sales, Tech support) mostly do not exist there. A team is ours: add
 * people by e-mail (before or after they first sign in), then say which
 * assistants the team may use and which were made for it.
 *
 * Two different things, kept apart on purpose:
 *   - **May use** — access. Members are admitted as if added by name: inside
 *     that assistant they read what its groups read, and nowhere else.
 *   - **Made for** — relevance. The assistant goes first on members' Hub;
 *     everyone else who may open it finds it under "More assistants". It
 *     never grants access.
 *   - **Membership** (ADR 0074) — a team links to one of the company's
 *     existing Entra groups (mostly "Team…"), picked by name. The group then
 *     IS its membership, nested groups included, and DOIT AI only READS it:
 *     joining and leaving happen in Entra, by the group's owner. A local
 *     team (no group) keeps its own member list — a test team for the
 *     transition, flagged in the compliance report. (ADR 0065's owner-
 *     approved writes remain for an `entra_write` team; none uses them.)
 *   - **Data they may read** (ADR 0064) — clearance. A data class the team
 *     is cleared for is readable wherever an assistant reads it. It decides
 *     neither entry nor actions, and needs the data owner's confirmation.
 *
 * Sign-in is still decided by the Entra entitlement group.
 */
import { vWrite } from "../admin-read-only";
import { computed, onMounted, ref, watch } from "vue";
import {
  AdminError,
  type EntraGroupHit,
  type Team,
  type TeamsResponse,
  addTeamMember,
  createTeam,
  deleteTeam,
  getTeams,
  grantTeamClearance,
  revokeTeamClearance,
  removeTeamMember,
  searchEntraGroups,
  setTeamAssistants,
  updateTeam,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import SkeletonRows from "./SkeletonRows.vue";

const props = defineProps<{ devUser: string }>();
const emit = defineEmits<{ (e: "changed"): void }>();
const toast = useToast();
const { confirm } = useConfirm();

const data = ref<TeamsResponse | null>(null);
const selected = ref<string | null>(null);
const busy = ref(false);

function fail(e: unknown, title: string): void {
  toast.error(title, e instanceof AdminError ? e.message : String(e));
}

async function run(
  title: string,
  fn: () => Promise<TeamsResponse>,
): Promise<boolean> {
  busy.value = true;
  try {
    data.value = await fn();
    emit("changed");
    return true;
  } catch (e) {
    fail(e, title);
    return false;
  } finally {
    busy.value = false;
  }
}

onMounted(() => run("Could not load teams", () => getTeams(props.devUser)));

const team = computed<Team | null>(
  () => data.value?.teams.find((t) => t.slug === selected.value) ?? null,
);
const names = computed(
  () => new Map((data.value?.assistants ?? []).map((a) => [a.slug, a.name])),
);

// ── New team ──────────────────────────────────────────────────────────────

const newName = ref("");
async function create(): Promise<void> {
  const name = newName.value.trim();
  if (!name) return;
  if (
    await run("Could not create the team", () =>
      createTeam({ name, description: "" }, props.devUser),
    )
  ) {
    newName.value = "";
    selected.value =
      data.value?.teams.find((t) => t.name === name)?.slug ?? null;
  }
}

// ── Members ───────────────────────────────────────────────────────────────

const newEmail = ref("");
async function addMember(): Promise<void> {
  const t = team.value;
  const email = newEmail.value.trim();
  if (!t || !email) return;
  if (
    await run(writeMode.value ? "Could not request it" : "Could not add them", () =>
      addTeamMember(t.slug, { email, reason: reason.value.trim() }, props.devUser),
    )
  ) {
    newEmail.value = "";
    reason.value = "";
    if (writeMode.value) toast.success("Request recorded", "An owner approves it under My sign-offs.");
  }
}

async function removeMember(email: string): Promise<void> {
  const t = team.value;
  if (!t) return;
  if (writeMode.value) {
    const ok = await confirm({
      title: `Request removing ${email}?`,
      message: "An owner approves it; DOIT AI then removes them from the Entra group.",
      confirmLabel: "Request removal",
      danger: true,
    });
    if (!ok) return;
  }
  await run("Could not remove them", () =>
    removeTeamMember(t.slug, email, props.devUser),
  );
}

// ── Assistants ────────────────────────────────────────────────────────────

async function toggle(slug: string, what: "access" | "made_for") {
  const t = team.value;
  if (!t) return;
  const access = new Set(t.access);
  const madeFor = new Set(t.made_for);
  const set = what === "access" ? access : madeFor;
  if (set.has(slug)) set.delete(slug);
  else set.add(slug);
  await run("Could not save", () =>
    setTeamAssistants(
      t.slug,
      { access: [...access], made_for: [...madeFor] },
      props.devUser,
    ),
  );
}

// ── Membership: the Entra group (ADR 0074) ────────────────────────────────

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Read-only: the group is the membership and nothing here changes it. */
const readOnly = computed(() => team.value?.membership_mode === "entra");
/** ADR 0065 writes (owner-approved requests) — kept, unused today. */
const writeMode = computed(() => team.value?.membership_mode === "entra_write");

const linkOwners = ref("");
const reason = ref("");
watch(
  () => [team.value?.slug, team.value?.owners?.join()],
  () => {
    linkOwners.value = (team.value?.owners ?? []).join(", ");
  },
  { immediate: true },
);
const pending = computed(() =>
  (team.value?.requests ?? []).filter((r) => ["pending", "approved", "failed"].includes(r.status)),
);
const recentDone = computed(() =>
  (team.value?.requests ?? []).filter((r) => ["applied", "rejected"].includes(r.status)).slice(0, 8),
);

// The picker: search existing groups by name; a pasted object id works too.
const groupQuery = ref("");
const groupHits = ref<EntraGroupHit[]>([]);
const groupError = ref("");
const searching = ref(false);
const picking = ref(false);
let searchTimer: ReturnType<typeof setTimeout> | undefined;
let searchSeq = 0;

async function searchGroups(): Promise<void> {
  const q = groupQuery.value.trim();
  const seq = ++searchSeq;
  if (GUID_RE.test(q)) {
    groupHits.value = [];
    groupError.value = "";
    return;
  }
  searching.value = true;
  try {
    const out = await searchEntraGroups(q, props.devUser);
    if (seq !== searchSeq) return;
    groupHits.value = out.groups;
    groupError.value = out.error;
  } catch (e) {
    if (seq !== searchSeq) return;
    groupHits.value = [];
    groupError.value = e instanceof AdminError ? e.message : String(e);
  } finally {
    if (seq === searchSeq) searching.value = false;
  }
}
watch(groupQuery, () => {
  if (!picking.value) return;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(searchGroups, 300);
});
function startPicking(): void {
  picking.value = true;
  groupQuery.value = "";
  void searchGroups();
}
watch(() => team.value?.slug, () => {
  picking.value = false;
  groupHits.value = [];
});

async function linkTo(groupId: string, groupName: string): Promise<void> {
  const t = team.value;
  if (!t || !groupId) return;
  const ok = await confirm({
    title: `Link ${t.name} to ${groupName || groupId}?`,
    message:
      "From now on the Entra group IS the membership: only people in it (nested groups " +
      "included) get what this team grants. DOIT AI only reads the group — joining and " +
      "leaving are done in Entra by the group's owner." +
      (t.members.length
        ? ` The ${t.members.length} people listed here stay on record but no longer count.`
        : ""),
    confirmLabel: "Link",
  });
  if (!ok) return;
  if (
    await run("Could not link", () =>
      updateTeam(t.slug, { entra_group_id: groupId.toLowerCase() }, props.devUser),
    )
  )
    picking.value = false;
}

async function unlink(): Promise<void> {
  const t = team.value;
  if (!t) return;
  const ok = await confirm({
    title: `Unlink ${t.name} from ${t.entra_group_name || t.entra_group_id}?`,
    message:
      "It becomes a local test team again: its own member list counts" +
      (t.members.length ? ` (${t.members.length} people)` : " (empty)") +
      ", and it is a finding in the compliance report while it grants anything.",
    confirmLabel: "Unlink",
    danger: true,
  });
  if (!ok) return;
  await run("Could not unlink", () =>
    updateTeam(t.slug, { entra_group_id: "" }, props.devUser),
  );
}

async function saveOwners(): Promise<void> {
  const t = team.value;
  if (!t) return;
  const owners = linkOwners.value
    .split(/[\s,;]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
  await run("Could not save", () => updateTeam(t.slug, { owners }, props.devUser));
}

// ── Data they may read (ADR 0064) ─────────────────────────────────────────

const classes = computed(() => data.value?.clearance_classes ?? []);
const classByToken = computed(() => new Map(classes.value.map((c) => [c.token, c])));
const grantable = computed(() =>
  classes.value.filter((c) => !team.value?.clearances.some((g) => g.token === c.token)),
);
const newClass = ref("");
const newOwner = ref("");
const newNote = ref("");
const pickedClass = computed(() => classByToken.value.get(newClass.value) ?? null);

function readers(tokens: string[]): string {
  return tokens.map((s) => names.value.get(s) ?? s).join(", ");
}

async function grant(): Promise<void> {
  const t = team.value;
  const c = pickedClass.value;
  if (!t || !c || !newOwner.value.trim()) return;
  const ok = await confirm({
    title: `Clear ${t.name} for ${c.label}?`,
    message:
      `${headcount(t)} ${headcount(t) === 1 ? "person" : "people"} will read ${c.label.toLowerCase()} data` +
      (c.assistants.length ? ` in ${readers(c.assistants)}` : "") +
      ` — wherever they may use those assistants. Recorded as confirmed by ${newOwner.value.trim()}.`,
    confirmLabel: "Grant",
  });
  if (!ok) return;
  if (
    await run("Could not grant", () =>
      grantTeamClearance(
        t.slug,
        { token: c.token, data_owner: newOwner.value.trim(), note: newNote.value.trim() },
        props.devUser,
      ),
    )
  ) {
    newClass.value = "";
    newOwner.value = "";
    newNote.value = "";
  }
}

async function revoke(token: string): Promise<void> {
  const t = team.value;
  if (!t) return;
  const label = classByToken.value.get(token)?.label ?? token;
  const ok = await confirm({
    title: `Remove ${label} from ${t.name}?`,
    message: "Members stop reading it on their next question.",
    confirmLabel: "Remove",
    danger: true,
  });
  if (!ok) return;
  await run("Could not remove", () => revokeTeamClearance(t.slug, token, props.devUser));
}

/** Who the team is today: the Entra group's people when linked. */
function headcount(t: Team): number {
  return t.entra_group_id ? t.entra_members.length : t.members.length;
}

// ── Rename / delete ───────────────────────────────────────────────────────

async function rename(t: Team, name: string, description: string) {
  if (name.trim() === t.name && description.trim() === t.description) return;
  await run("Could not save", () =>
    updateTeam(t.slug, { name, description }, props.devUser),
  );
}

async function remove(t: Team): Promise<void> {
  const ok = await confirm({
    title: `Delete ${t.name}?`,
    message: `${headcount(t)} ${headcount(t) === 1 ? "person loses" : "people lose"} what this team gave them, at once${t.access.length ? ` — access to ${t.access.map((s) => names.value.get(s) ?? s).join(", ")}` : ""}. The deletion is in the audit log.`,
    confirmLabel: "Delete team",
    danger: true,
  });
  if (!ok) return;
  if (await run("Could not delete", () => deleteTeam(t.slug, props.devUser)))
    selected.value = null;
}
</script>

<template>
  <div class="teams">
    <p class="intro">
      A team follows one of the company's Entra groups — who is in it is
      decided in Entra, and DOIT AI only reads it. Give a team the assistants
      it <strong>may use</strong>, and mark the ones <strong>made for</strong>
      it — those go first on its members' Hub. Signing in is still decided by
      the Users group in Entra.
    </p>

    <SkeletonRows v-if="!data" :rows="4" />
    <div v-else class="teams__body">
      <aside class="teams__list">
        <form v-write class="new" @submit.prevent="create">
          <input
            v-model="newName"
            placeholder="New team, e.g. Sales"
            maxlength="200"
          />
          <button class="btn" :disabled="busy || !newName.trim()">
            Create
          </button>
        </form>
        <button
          v-for="t in data.teams"
          :key="t.slug"
          :class="['team', selected === t.slug && 'team--on']"
          @click="selected = t.slug"
        >
          <span class="team__name">{{ t.name }}</span>
          <span class="muted small"
            >{{ t.entra_group_id ? `${t.entra_group_name || "Entra"} · ` : "local · " }}{{ headcount(t) }}
            {{ headcount(t) === 1 ? "person" : "people" }} ·
            {{
              t.access.length +
              t.made_for.filter((s) => !t.access.includes(s)).length
            }}
            {{ "assistants" }}</span
          >
        </button>
        <p v-if="!data.teams.length" class="muted small empty">No teams yet.</p>
      </aside>

      <section v-if="team" class="teams__detail">
        <div class="detail__head">
          <input
            class="detail__name"
            :value="team.name"
            maxlength="200"
            aria-label="Team name"
            v-write
            @change="
              rename(
                team,
                ($event.target as HTMLInputElement).value,
                team.description,
              )
            "
          />
          <button v-write class="btn btn--ghost" :disabled="busy" @click="remove(team)">
            Delete team
          </button>
        </div>
        <input
          class="detail__desc"
          :value="team.description"
          placeholder="What this team does (optional)"
          maxlength="1000"
          v-write
          @change="
            rename(team, team.name, ($event.target as HTMLInputElement).value)
          "
        />

        <h4>Membership</h4>
        <template v-if="team.entra_group_id">
          <p class="muted small">
            Follows the Entra group
            <strong>{{ team.entra_group_name || team.entra_group_id }}</strong>
            <span class="mono">({{ team.entra_group_id }})</span>: everyone in it, nested
            groups included, gets what this team grants.
            <template v-if="readOnly">
              DOIT AI only reads the group — joining and leaving are done in Entra<template
                v-if="team.entra_owners.length"
              >, by its owner{{ team.entra_owners.length === 1 ? "" : "s" }}
                {{ team.entra_owners.map((o) => o.name || o.email).join(", ") }}</template
              >. Changes count within about ten minutes.
            </template>
            <template v-else>
              Joining and leaving are requests its owners approve under My sign-offs; DOIT AI
              then writes the group (ADR 0065).
            </template>
          </p>
          <div class="link-actions">
            <button v-write class="btn btn--ghost" :disabled="busy" @click="startPicking">
              Change group
            </button>
            <button v-write class="btn btn--ghost" :disabled="busy" @click="unlink">
              Unlink
            </button>
          </div>
        </template>
        <template v-else>
          <p class="warn-text small">
            Local test team — its members are kept here, not in Entra. That is for the
            transition only: link the company's Entra group for this team, so membership is
            decided in Entra and recorded where Group IT reviews access.
          </p>
          <div v-if="!picking" class="link-actions">
            <button v-write class="btn" :disabled="busy" @click="startPicking">
              Link an Entra group…
            </button>
          </div>
        </template>
        <div v-if="picking" class="picker">
          <input
            v-model="groupQuery"
            placeholder="Search groups by name, e.g. Team Sales — or paste an object id"
            maxlength="100"
            aria-label="Search Entra groups"
          />
          <p v-if="groupError" class="warn-text small">{{ groupError }}</p>
          <ul class="members picker__hits">
            <li v-if="GUID_RE.test(groupQuery.trim())">
              <span class="mono">{{ groupQuery.trim().toLowerCase() }}</span>
              <button v-write class="link" :disabled="busy" @click="linkTo(groupQuery.trim(), '')">
                use this object id
              </button>
            </li>
            <li v-for="g in groupHits" :key="g.id">
              <span>
                <strong>{{ g.name }}</strong>
                <span v-if="g.description" class="muted small"> · {{ g.description }}</span>
              </span>
              <button
                v-write
                class="link"
                :disabled="busy || g.id === team.entra_group_id"
                @click="linkTo(g.id, g.name)"
              >
                {{ g.id === team.entra_group_id ? "linked" : "link" }}
              </button>
            </li>
            <li v-if="searching" class="muted small">Searching…</li>
            <li
              v-else-if="!groupHits.length && !GUID_RE.test(groupQuery.trim())"
              class="muted small"
            >
              No group starts with that name.
            </li>
          </ul>
          <button class="link muted" @click="picking = false">cancel</button>
        </div>

        <datalist id="team-known-users">
          <option v-for="u in data.known_users" :key="u.email" :value="u.email">
            {{ u.name }}
          </option>
        </datalist>
        <template v-if="team.entra_group_id">
          <h4>In the Entra group · {{ team.entra_members.length }}</h4>
          <p v-if="team.entra_error" class="warn-text small">{{ team.entra_error }}</p>
          <ul class="members">
            <li v-for="m in team.entra_members" :key="m.oid">
              <span>{{ m.name || m.email }}</span>
              <span v-if="m.name" class="muted small">{{ m.email }}</span>
              <button
                v-if="writeMode"
                v-write
                class="link muted"
                :disabled="busy || !m.email"
                @click="removeMember(m.email)"
              >
                request removal
              </button>
            </li>
          </ul>
          <p v-if="readOnly && team.members.length" class="muted small">
            {{ team.members.length }} {{ team.members.length === 1 ? "person was" : "people were" }}
            listed here before the link — kept on record, no longer counted.
          </p>
          <template v-if="writeMode">
            <div class="link-grid">
              <label>
                Owners — approve joining and leaving
                <input v-model="linkOwners" placeholder="e-mails, comma separated" />
              </label>
              <button v-write class="btn" :disabled="busy" @click="saveOwners">Save</button>
            </div>
            <p v-if="!team.owners.length" class="warn-text small">
              No owner named — until there is one, an admin decides requests.
            </p>
            <template v-if="pending.length">
              <h4>Waiting · {{ pending.length }}</h4>
              <ul class="members">
                <li v-for="r in pending" :key="r.id">
                  <span>{{ r.action === "add" ? "Add" : "Remove" }} {{ r.email }}</span>
                  <span class="muted small">
                    asked by {{ r.requested_by }}
                    <template v-if="r.status === 'failed'"> · <span class="warn-text">not written: {{ r.error }}</span></template>
                  </span>
                </li>
              </ul>
            </template>
            <p v-if="recentDone.length" class="muted small">
              Recent:
              <span v-for="r in recentDone" :key="r.id">
                {{ r.action }} {{ r.email }} — {{ r.status }} by {{ r.decided_by }};
              </span>
            </p>
            <form v-write class="new" @submit.prevent="addMember">
              <input
                v-model="newEmail"
                type="email"
                list="team-known-users"
                placeholder="name@danofficeit.com"
                maxlength="320"
              />
              <input v-model="reason" placeholder="Why (for the owner)" maxlength="1000" />
              <button class="btn" :disabled="busy || !newEmail.trim()">Request</button>
            </form>
          </template>
        </template>
        <template v-else>
        <h4>People · {{ team.members.length }}</h4>
        <ul class="members">
          <li v-for="m in team.members" :key="m.email">
            <span>{{ m.name || m.email }}</span>
            <span v-if="m.name" class="muted small">{{ m.email }}</span>
            <button
              class="link muted"
              :disabled="busy"
              v-write
              @click="removeMember(m.email)"
            >
              remove
            </button>
          </li>
        </ul>
        <form v-write class="new" @submit.prevent="addMember">
          <input
            v-model="newEmail"
            type="email"
            list="team-known-users"
            placeholder="name@danofficeit.com"
            maxlength="320"
          />
          <button class="btn" :disabled="busy || !newEmail.trim()">Add</button>
        </form>
        <p class="muted small">
          Anyone can be added by e-mail, also before they first sign in. They
          still need to be in the Entra Users group to sign in at all.
        </p>
        </template>

        <h4>Assistants</h4>
        <table class="grants">
          <thead>
            <tr>
              <th>Assistant</th>
              <th title="Members may open and use it">May use (access)</th>
              <th title="Put first on members' Hub; grants nothing">
                Made for (Hub order)
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in data.assistants" :key="a.slug">
              <td>{{ a.name }}</td>
              <td>
                <input
                  type="checkbox"
                  :checked="team.access.includes(a.slug)"
                  :disabled="busy"
                  :aria-label="`${team.name} may use ${a.name}`"
                  v-write
                  @change="toggle(a.slug, 'access')"
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  :checked="team.made_for.includes(a.slug)"
                  :disabled="busy"
                  :aria-label="`${a.name} is made for ${team.name}`"
                  v-write
                  @change="toggle(a.slug, 'made_for')"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <p class="muted small">
          "May use" matters only for assistants that are restricted — one
          everyone can open needs no team to be used. Giving a team an assistant
          where a whole group sees all of a source's data asks that source's
          data owner to confirm again.
        </p>

        <h4>Data they may read</h4>
        <p class="muted small">
          A data class lets members read it in every assistant they may use that
          reads it — not anywhere else, and it opens no assistant by itself.
          Without one, members read what each assistant's own groups read.
        </p>
        <ul v-if="team.clearances.length" class="members">
          <li v-for="g in team.clearances" :key="g.token">
            <span>
              <strong>{{ classByToken.get(g.token)?.label ?? g.token }}</strong>
              <span class="muted small">
                · confirmed by {{ g.data_owner }} · {{ g.granted_at.slice(0, 10) }}</span
              >
            </span>
            <button v-write class="link muted" :disabled="busy" @click="revoke(g.token)">
              remove
            </button>
          </li>
        </ul>
        <p v-else class="muted small">None — members read what each assistant's groups read.</p>
        <form v-write v-if="grantable.length" class="clear" @submit.prevent="grant">
          <select v-model="newClass" aria-label="Data class">
            <option value="">Choose a data class…</option>
            <option v-for="c in grantable" :key="c.token" :value="c.token">
              {{ c.label }}
            </option>
          </select>
          <input
            v-model="newOwner"
            placeholder="Confirmed by (data owner)"
            maxlength="320"
            aria-label="Data owner who confirmed"
          />
          <input v-model="newNote" placeholder="Note (optional)" maxlength="1000" aria-label="Note" />
          <button class="btn" :disabled="busy || !newClass || !newOwner.trim()">Grant</button>
        </form>
        <p v-if="pickedClass" class="muted small">
          {{ pickedClass.unlocks }}
          <template v-if="pickedClass.assistants.length">
            Read by: {{ readers(pickedClass.assistants) }}.
          </template>
        </p>
      </section>
      <p v-else class="muted teams__detail teams__detail--empty">
        Pick a team, or create one.
      </p>
    </div>
  </div>
</template>

<style scoped>
.teams {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.intro {
  margin: 0;
  max-width: 60rem;
  color: var(--color-muted);
  font-size: 0.9rem;
}
.teams__body {
  display: grid;
  grid-template-columns: minmax(14rem, 18rem) 1fr;
  gap: var(--space-4);
  align-items: start;
}
@media (max-width: 760px) {
  .teams__body {
    grid-template-columns: 1fr;
  }
}
.teams__list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.teams__list .empty {
  margin: 0.2rem 0 0;
}
.team {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  padding: 0.5rem 0.7rem;
  text-align: left;
  font: inherit;
  cursor: pointer;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: inherit;
}
.team--on {
  border-color: var(--color-accent);
  box-shadow: inset 3px 0 0 var(--color-accent);
}
.team__name {
  font-weight: 600;
}
/* One height for the create row, the add-member row and the empty panel,
   so the left column's first row lines up with the panel beside it. */
.new {
  display: flex;
  gap: 0.4rem;
  align-items: stretch;
}
.new input,
.new .btn {
  height: 2.5rem;
  box-sizing: border-box;
}
.new .btn {
  display: inline-flex;
  align-items: center;
}
.new input,
.detail__desc,
.detail__name {
  flex: 1;
  min-width: 0;
  font: inherit;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
}
.teams__detail {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: var(--space-3) var(--space-4);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
.teams__detail--empty {
  margin: 0;
  min-height: 2.5rem;
  box-sizing: border-box;
  justify-content: center;
  padding-top: 0;
  padding-bottom: 0;
}
.link-grid {
  display: grid;
  grid-template-columns: minmax(14rem, 1fr) auto;
  gap: 0.5rem;
  align-items: end;
}
.link-grid label {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.8rem;
  color: var(--color-muted);
}
.link-grid input {
  font: inherit;
  height: 2.5rem;
  box-sizing: border-box;
  padding: 0 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
}
.link-grid .btn {
  height: 2.5rem;
}
.link-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.picker {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
}
.picker input {
  font: inherit;
  height: 2.5rem;
  box-sizing: border-box;
  padding: 0 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
  min-width: 0;
}
.picker__hits {
  max-height: 16rem;
  overflow-y: auto;
}
.warn-text {
  color: var(--color-warning, #b54708);
}
.mono {
  font-family: var(--font-mono);
}
@media (max-width: 760px) {
  .link-grid {
    grid-template-columns: 1fr;
  }
}
.clear {
  display: grid;
  grid-template-columns: minmax(10rem, 14rem) minmax(10rem, 1fr) minmax(8rem, 1fr) auto;
  gap: 0.4rem;
}
.clear select,
.clear input {
  font: inherit;
  height: 2.5rem;
  box-sizing: border-box;
  padding: 0 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
  min-width: 0;
}
@media (max-width: 760px) {
  .clear {
    grid-template-columns: 1fr;
  }
}
.detail__head {
  display: flex;
  gap: 0.5rem;
}
.detail__name {
  font-size: 1.05rem;
  font-weight: 600;
}
.teams__detail h4 {
  margin: 0.6rem 0 0;
  font-size: 0.85rem;
}
.members {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.members li {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}
.members li button {
  margin-left: auto;
}
.grants {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.grants th {
  text-align: left;
  font-weight: 500;
  font-size: 0.75rem;
  color: var(--color-muted);
  padding: 0.25rem 0.5rem 0.25rem 0;
  border-bottom: 1px solid var(--color-border);
}
.grants td {
  padding: 0.3rem 0.5rem 0.3rem 0;
  border-bottom: 1px solid var(--color-border);
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.8rem;
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  padding: 0;
  font: inherit;
  font-size: 0.82rem;
}
button.link.muted {
  color: var(--color-muted);
}
</style>
